import { useMutation } from "@apollo/client";
import { client } from "../apolloClient";
import { gql } from "@apollo/client";
import { PROJECT_MILESTONE_QUERIES } from "./useProjectMilestone";
/**
 * useProjectActivity custom hook for project task/activity operations.
 * - Provides CRUD operations for project tasks using Apollo Client and GraphQL.
 * - Handles cache updates for project task queries and mutations.
 * - Exposes helpers for reading/writing/updating project tasks in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting tasks.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 * - Supports resource availability checks and task status updates.
 */

const PROJECT_ACTIVITY_QUERIES = {
  // Query to fetch all tasks
  GET_TASKS: gql`
    query GetTasks {
      GetTasks {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
  // Query to fetch tasks by project ID
  GET_TASKS_BY_PROJECT: gql`
    query GetTasksByProjectId($projectId: Int!) {
      GetTasksByProjectId(project_id: $projectId) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
  // Mutation to add a new task
  ADD_PROJECT_ACTIVITY: gql`
    mutation AddTask(
      $project_id: Int!
      $milestone_code: String!
      $parent_task_id: Int
      $task_name: String!
      $estimated_hours: Float
      $task_status_code: TaskStatusCode
      $start_date: String!
      $end_date: String!
      $created_by: Int!
      $resources: [TaskResourceInput]
    ) {
      AddTask(
        project_id: $project_id
        milestone_code: $milestone_code
        parent_task_id: $parent_task_id
        task_name: $task_name
        estimated_hours: $estimated_hours
        task_status_code: $task_status_code
        start_date: $start_date
        end_date: $end_date
        created_by: $created_by
        resources: $resources
      ) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
  // Mutation to update a task
  UPDATE_PROJECT_ACTIVITY: gql`
    mutation UpdateTask(
      $task_id: ID!
      $project_id: Int
      $milestone_code: String
      $parent_task_id: Int
      $task_name: String
      $estimated_hours: Float
      $task_status_code: TaskStatusCode
      $start_date: String
      $end_date: String
      $is_active: Boolean
      $resources: [TaskResourceInput]
    ) {
      UpdateTask(
        task_id: $task_id
        project_id: $project_id
        milestone_code: $milestone_code
        parent_task_id: $parent_task_id
        task_name: $task_name
        estimated_hours: $estimated_hours
        task_status_code: $task_status_code
        start_date: $start_date
        end_date: $end_date
        is_active: $is_active
        resources: $resources
      ) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
  // Mutation to delete a task
  DELETE_PROJECT: gql`
    mutation DeleteTask($task_id: ID!) {
      DeleteTask(task_id: $task_id) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
  // Mutation to check resource availability for a task
  CHECK_RESOURCE_AVAILABILITY: gql`
    mutation CheckResourceAvailability(
      $start_date: String!
      $end_date: String!
      $resources: [TaskResourceInput]!
      $task_id: ID
    ) {
      CheckResourceAvailability(
        start_date: $start_date
        end_date: $end_date
        resources: $resources
        task_id: $task_id
      ) {
        success
        resources {
          resource_id
          is_available
          required_hours
          available_slots {
            date
            available_hours
            can_allocate
          }
          message
        }
      }
    }
  `,
  // Query to fetch tasks by project and resource ID
  GET_TASKS_BY_PROJECT_RESOURCE_ID: gql`
    query GetTasksByProjectResourceId($projectId: Int!, $resourceId: Int!) {
      GetTasksByProjectResourceId(
        project_id: $projectId
        resource_id: $resourceId
      ) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
  // Mutation to update task status
  UPDATE_TASK_STATUS: gql`
    mutation UpdateTaskStatus($task_id: ID!, $task_status_code: TaskStatusCode!) {
      UpdateTaskStatus(task_id: $task_id, task_status_code: $task_status_code) {
        task_id
        task_status_code
        updated_on
      }
    }
  `,
};

// Cache operations for project tasks
const cacheOperations = {
  readTasks: () => {
    try {
      const { GetTasks = [] } =
        client.readQuery({
          query: PROJECT_ACTIVITY_QUERIES.GET_TASKS,
        }) || {};
      return GetTasks;
    } catch {
      return [];
    }
  },
  readProjectTasks: (projectId) => {
    try {
      const { GetTasksByProjectId = [] } =
        client.readQuery({
          query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
          variables: { projectId },
        }) || {};
      return GetTasksByProjectId;
    } catch {
      return [];
    }
  },
  writeTasks: (tasks) => {
    client.writeQuery({
      query: PROJECT_ACTIVITY_QUERIES.GET_TASKS,
      data: { GetTasks: tasks },
    });
  },
  writeProjectTasks: (projectId, tasks) => {
    client.writeQuery({
      query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
      variables: { projectId },
      data: { GetTasksByProjectId: tasks },
    });
  },
};

export const useProjectActivity = () => {
  // Mutation for adding a task
  const [addTaskMutation] = useMutation(
    PROJECT_ACTIVITY_QUERIES.ADD_PROJECT_ACTIVITY,
    {
      update(cache, { data: { AddTask } }) {
        try {
          const projectId = parseInt(AddTask.project_id);

          const existingProjectData = cache.readQuery({
            query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
            variables: { projectId },
          });
          if (existingProjectData) {
            // Remove any temporary tasks and add the new task
            const filteredTasks = (existingProjectData.GetTasksByProjectId || []).filter(
              (task) => !task.task_id.toString().startsWith("temp-")
            );
            
            cache.writeQuery({
              query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
              variables: { projectId },
              data: {
                GetTasksByProjectId: [AddTask, ...filteredTasks],
              },
            });
          }

          // Update milestones as well (since they are from the same table now)
          const existingMilestones = cache.readQuery({
            query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
          });

          if (existingMilestones?.GetProjectMilestone) {
            cache.writeQuery({
              query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
              data: {
                GetProjectMilestone: [AddTask, ...existingMilestones.GetProjectMilestone],
              },
            });
          }
        } catch (error) {
          console.error("Cache update error:", error);
        }
      },
    }
  );

  // Mutation for updating a task
  const [updateTaskMutation] = useMutation(
    PROJECT_ACTIVITY_QUERIES.UPDATE_PROJECT_ACTIVITY,
    {
      update(cache, { data: { UpdateTask } }) {
        try {
          const projectId = parseInt(UpdateTask.project_id);
          const existingProjectData = cache.readQuery({
            query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
            variables: { projectId },
          });

          if (existingProjectData?.GetTasksByProjectId) {
            const updatedTasks =
             existingProjectData.GetTasksByProjectId.map((task) =>
              task.task_id === UpdateTask.task_id ? UpdateTask : task
            );

            cache.writeQuery({
              query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
              variables: { projectId },
              data: { GetTasksByProjectId: updatedTasks },
            });
          }

          // Update milestones cache
          const existingMilestones = cache.readQuery({
            query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
          });

          if (existingMilestones?.GetProjectMilestone) {
            const updatedMilestones = existingMilestones.GetProjectMilestone.map((milestone) =>
              milestone.task_id === UpdateTask.task_id ? UpdateTask : milestone
            );

            cache.writeQuery({
              query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
              data: { GetProjectMilestone: updatedMilestones },
            });
          }
        } catch (error) {
          console.error("Error updating cache after updating task:", error);
        }
      },
    }
  );

  // Mutation for deleting a task
  const [deleteTaskMutation] = useMutation(
    PROJECT_ACTIVITY_QUERIES.DELETE_PROJECT,
    {
      update(cache, { data: { DeleteTask } }, { variables }) {
        try {
          const projectId = parseInt(DeleteTask.project_id);

          // Remove from tasks cache
          const existingProjectData = cache.readQuery({
            query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
            variables: { projectId },
          });

          if (existingProjectData?.GetTasksByProjectId) {
            const filteredTasks = existingProjectData.GetTasksByProjectId.filter(
              (task) => task.task_id !== variables.task_id
            );

            cache.writeQuery({
              query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
              variables: { projectId },
              data: { GetTasksByProjectId: filteredTasks },
            });
          }

          // Remove from milestones cache
          const existingMilestones = cache.readQuery({
            query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
          });

          if (existingMilestones?.GetProjectMilestone) {
            const filteredMilestones = existingMilestones.GetProjectMilestone.filter(
              (milestone) => milestone.task_id !== variables.task_id
            );

            cache.writeQuery({
              query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
              data: { GetProjectMilestone: filteredMilestones },
            });
          }
        } catch (error) {
          console.error("Error updating cache after deleting task:", error);
        }
      },
    }
  );

  // Mutation for checking resource availability
  const [checkResourceAvailabilityMutation] = useMutation(
    PROJECT_ACTIVITY_QUERIES.CHECK_RESOURCE_AVAILABILITY
  );

  // Mutation for updating task status
  const [updateTaskStatusMutation] = useMutation(PROJECT_ACTIVITY_QUERIES.UPDATE_TASK_STATUS, {
    update(cache, { data: { UpdateTaskStatus } }) {
      try {
        const projectId = parseInt(UpdateTaskStatus.project_id);
        const existingProjectData = cache.readQuery({
          query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
          variables: { projectId },
        });

        if (existingProjectData?.GetTasksByProjectId) {
          const updatedProjectTasks = existingProjectData.GetTasksByProjectId.map((task) =>
            task.task_id === UpdateTaskStatus.task_id
              ? {
                  ...task,
                  task_status_code: UpdateTaskStatus.task_status_code,
                  updated_on: UpdateTaskStatus.updated_on,
                }
              : task
          );

          cache.writeQuery({
            query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
            variables: { projectId },
            data: {
              GetTasksByProjectId: updatedProjectTasks,
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after status update:", error);
      }
    },
  });

  /**
   * Fetches all tasks from the backend.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getTasks = async () => {
    try {
      const { data } = await client.query({
        query: PROJECT_ACTIVITY_QUERIES.GET_TASKS,
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetTasks) {
        throw new Error("No tasks data received");
      }

      return { success: true, data: data.GetTasks };
    } catch (error) {
      console.error("Task fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches all tasks for a given project.
   * @param {number|string} projectId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getTasksByProject = async (projectId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT,
        variables: { projectId },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetTasksByProjectId) {
        throw new Error("No tasks data received");
      }

      return { success: true, data: data.GetTasksByProjectId };
    } catch (error) {
      console.error("Project tasks fetch error:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new task.
   * @param {Object} formData - Task form data.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createTask = async (formData) => {
    try {
      const { data } = await addTaskMutation({
        variables: {
          project_id: formData.project_id,
          milestone_code: formData.milestone_code,
          parent_task_id: formData.parent_task_id,
          task_name: formData.task_name,
          estimated_hours: formData.estimated_hours,
          task_status_code: formData.task_status_code,
          start_date: formData.start_date,
          end_date: formData.end_date,
          created_by: formData.created_by,
          resources: formData.resources,
        },
      });

      if (data?.AddTask) {
        return { success: true, data: data.AddTask };
      } else {
        throw new Error("Failed to create task");
      }
    } catch (error) {
      console.error("Error creating task:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates an existing task.
   * @param {string|number} taskId
   * @param {Object} formData - Task form data.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateTask = async (taskId, formData) => {
    try {
      const { data } = await updateTaskMutation({
        variables: {
          task_id: taskId.toString(),
          project_id: formData.project_id,
          milestone_code: formData.milestone_code,
          parent_task_id: formData.parent_task_id,
          task_name: formData.task_name,
          estimated_hours: formData.estimated_hours,
          task_status_code: formData.task_status_code,
          start_date: formData.start_date,
          end_date: formData.end_date,
          is_active: formData.is_active,
          resources: formData.resources,
        },
      });

      if (data?.UpdateTask) {
        return { success: true, data: data.UpdateTask };
      } else {
        throw new Error("Failed to update task");
      }
    } catch (error) {
      console.error("Error updating task:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Deletes a task by its ID.
   * @param {string|number} taskId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteTask = async (taskId) => {
    try {
      const { data } = await deleteTaskMutation({
        variables: { task_id: taskId.toString() },
      });

      return { success: true, data: data.DeleteTask };
    } catch (error) {
      console.error("Error deleting task:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Checks resource availability for a task.
   * @param {string} startDate
   * @param {string} endDate
   * @param {Array} resources
   * @param {string|number} taskId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const checkResourceAvailability = async (
    startDate,
    endDate,
    resources,
    taskId
  ) => {
    try {
      const { data } = await checkResourceAvailabilityMutation({
        variables: {
          start_date: startDate,
          end_date: endDate,
          resources,
          task_id: taskId,
        },
      });

      if (data?.CheckResourceAvailability) {
        return {
          success: true,
          data: data.CheckResourceAvailability,
        };
      } else {
        throw new Error("Failed to check resource availability");
      }
    } catch (error) {
      console.error("Error checking resource availability:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  };

  /**
   * Fetches tasks by project and resource ID.
   * @param {string|number} projectId
   * @param {string|number} resourceId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getTasksByProjectResourceId = async (projectId, resourceId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_ACTIVITY_QUERIES.GET_TASKS_BY_PROJECT_RESOURCE_ID,
        variables: {
          projectId: parseInt(projectId),
          resourceId: parseInt(resourceId),
        },
        fetchPolicy: "network-only",
        nextFetchPolicy: "cache-first",
      });

      if (!data?.GetTasksByProjectResourceId) {
        throw new Error(
          "No tasks data received for the given project and resource ID"
        );
      }

      return { success: true, data: data.GetTasksByProjectResourceId };
    } catch (error) {
      console.error("Error fetching tasks by project and resource ID:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates the status of a task.
   * @param {string|number} taskId
   * @param {string} status
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateTaskStatus = async (taskId, status) => {
    try {
      const { data } = await updateTaskStatusMutation({
        variables: {
          task_id: taskId.toString(),
          task_status_code: status,
        },
      });

      if (data?.UpdateTaskStatus) {
        return { success: true, data: data.UpdateTaskStatus };
      } else {
        throw new Error("Failed to update task status");
      }
    } catch (error) {
      console.error("Error updating task status:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getTasks,
    getTasksByProject,
    createTask,
    updateTask,
    deleteTask,
    cacheOperations,
    checkResourceAvailability,
    getTasksByProjectResourceId,
    updateTaskStatus,
  };
};
